import { height, width } from './general';
import { CareItem } from 'src/replicant/care/care.ruleset';
import popups from './popups';
import buttons from './buttons';
import { assertNever } from 'src/replicant/utils';
import { degreesToRadians } from 'src/replicant/utils/convert';
import { TutorialStep } from 'src/replicant/tutorial/tutorial.ruleset';
import { Locale } from 'src/replicant/ruleset/locale';
import { ImageScaleViewOpts } from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { FlexBoxImageScaleViewOpts } from '@play-co/timestep-core/flexbox';
import { downscale } from 'src/conf/scale';

const careItemIcons: Record<CareItem['item'], string> =
  // prettier-ignore
  {
    // ---------------------
    // Toys
    // ---------------------
    commonBell: 'assets/ui/care/care_item_toy_CommonBell.png',
    miracleBall: 'assets/ui/care/care_item_toy_MiracleBall.png',
    woollyCaterpillar: 'assets/ui/care/care_item_toy_WoollyCaterpillar.png',
    noSurpriseJackInTheBox: 'assets/ui/care/care_item_toy_JackintheBox.png',
    fairytaleUnicorn: 'assets/ui/care/care_item_toy_Unicorn.png',
    autoPilotAirplane: 'assets/ui/care/care_item_toy_PiilotAirplane.png',
    emptyMagicLamp: 'assets/ui/care/care_item_toy_MagicLamp.png',
    doremiPiano: 'assets/ui/care/care_item_toy_piano.png',

    // ---------------------
    // Food
    // ---------------------
    buruburuFood: 'assets/ui/care/care_item_food_Buruburu.png',
    gentleSushi: 'assets/ui/care/care_item_food_Sushi.png',
    skewedSalad: 'assets/ui/care/care_item_food_Salad.png',
    attractiveCurryAndRice: 'assets/ui/care/care_item_food_Curry&Rice.png',
    tooSpicyTteokbokki: 'assets/ui/care/care_item_food_Tteokbokki.png',
    meatLessBurger: 'assets/ui/care/care_item_food_MeatLessBurger.png',
    dimsumTower: 'assets/ui/care/care_item_food_DimSumTower.png',
    edibleIsland: 'assets/ui/care/care_item_food_EdibleIsland.png',

    // ---------------------
    // Snack
    // ---------------------
    rainbowCottonCandy: 'assets/ui/care/care_item_snack_RainbowCottonCandy.png',
    candiesFromPocket: 'assets/ui/care/care_item_snack_CandiesfromPocket.png',
    tooHardRiceCracker: 'assets/ui/care/care_item_snack_TooHardRiceCracker.png',
    dreamingBerryParfait: 'assets/ui/care/care_item_snack_DreamingBerryParfait.png',
    expensiveFruits: 'assets/ui/care/care_item_snack_ExpensiveFruits.png',
    wishFulfillingMontBlanc: 'assets/ui/care/care_item_snack_Wishfulfilling_MontBlanc.png',
    mameDaifuku: 'assets/ui/care/care_item_snack_MameDaifuku.png',
    bucketPudding: 'assets/ui/care/care_item_snack_BucketPudding.png',

    // ---------------------
    // Medicine
    // ---------------------
    capsule: 'assets/ui/care/care_item_firstaid_Capsule.png',
    whiteTooth: 'assets/ui/care/care_item_firstaid_WhiteTooth.png',
    superCapsule: 'assets/ui/care/care_item_firstaid_SuperCapsule.png',
    shiningCapsule: 'assets/ui/care/care_item_firstaid_ShiningTooth.png',

    perfume: 'assets/ui/care/care_item_firstaid_PanaceaColon.png',
    superPerfume: 'assets/ui/care/care_item_firstaid_GoldPanacea.png',

    // ---------------------
    // Invitation
    // ---------------------

    vowsCoffee: 'assets/ui/care/care_item_invitation_coffe.png',
    ultimateVowsCoffee: 'assets/ui/care/care_item_invitation_ultimate_coffe.png',
  };

export function getCareItemAnimationName<T extends CareItem['item']>(
  careItemType: T,
) {
  switch (careItemType) {
    case 'gentleSushi':
    case 'buruburuFood':
    case 'attractiveCurryAndRice':
    case 'skewedSalad':
    case 'meatLessBurger':
    case 'tooSpicyTteokbokki':
    case 'edibleIsland':
    case 'dimsumTower':
    case 'candiesFromPocket':
    case 'rainbowCottonCandy':
    case 'dreamingBerryParfait':
    case 'tooHardRiceCracker':
    case 'expensiveFruits':
    case 'wishFulfillingMontBlanc':
    case 'bucketPudding':
    case 'mameDaifuku':
    case 'commonBell':
    case 'miracleBall':
    case 'woollyCaterpillar':
    case 'noSurpriseJackInTheBox':
    case 'autoPilotAirplane':
    case 'fairytaleUnicorn':
    case 'emptyMagicLamp':
    case 'capsule':
    case 'superCapsule':
    case 'whiteTooth':
      return careItemType;

    case 'doremiPiano':
      return 'piano';

    case 'shiningCapsule':
      return 'shiningTooth';

    case 'perfume':
      return 'panaceaColon';

    case 'superPerfume':
      return 'goldPanaceaColon';

    case 'vowsCoffee':
      return 'acceptance_coffee';

    case 'ultimateVowsCoffee':
      return 'acceptance_ultimateCoffee';

    default:
      assertNever(careItemType);
  }
}

namespace confirmCareItem {
  const popup = {
    zIndex: 10000,
    width: popups.buruburu.width,
    height: 594,
    darkerBg: false,
  };

  const message = {
    height: 50,
    x: 40,
    y: 80,
    zIndex: 1,
  };

  const underline = <const>{
    ...popups.underline,
    x: message.x,
  };

  const careItemPanel = <const>{
    image: 'assets/ui/care/care_item_description_panel.png',
    height: 182,
    width: 491,
    x: (popup.width - 491) / 2,
    y: 150,
  };

  const currency = <const>{
    scaleMethod: 'contain',
    image: 'assets/ui/hud/icons/hud_coin.png',
    y: -4,
    width: 58,
    height: 58,
  };

  const highlightRow = <const>{
    x: 40,
    width: popup.width - 80,
    height: 50,
    image: 'assets/ui/popups/panel_highlight.png',
    slices: {
      top: 9,
      bot: 9,
      left: 9,
      right: 9,
    },
  };
  const highlightRowLabel = <const>{
    x: 10,
    size: 22,
    font: 'Body',
    color: '#BF8147',
    height: highlightRow.height,
    width: 300,
    verticalAlign: 'center',
  };
  const highlightRowValue = <const>{
    size: 22,
    font: 'Body',
    color: '#9A7171',
    height: 50,
    width: 130,
    x: highlightRow.width - 130 - currency.width - 10,
    verticalAlign: 'center',
    align: 'right',
  };

  const costRow = {
    ...highlightRow,
    y: 350,
  };

  const balanceRow = {
    ...highlightRow,
    y: 418,
  };

  const commandButton = <const>{
    fontSize: 40,
    labelOffsetY: -2,
    font: 'Body',
    y: 594 - 15 - 90,
    width: 235,
    height: 90,
  };
  const backButton = {
    ...buttons.cancel,
    ...commandButton,
    x: 40,
  };
  const okButton = {
    ...buttons.secondary,
    ...commandButton,
    x: 304,
  };

  export const ui = {
    popup,
    message,
    underline,
    careItemPanel,
    careItemIcons,
    costRow,
    balanceRow,
    highlightRowLabel,
    highlightRowValue,
    currency,
    backButton,
    okButton,
  };
}

namespace bedroom {
  const bedroom = {
    image: 'assets/ui/bedroom/bedroom.png',
  };
  const bedroomBed = {
    image: 'assets/ui/bedroom/bedroom_bed.png',
    width: 490,
    height: 319,
    centerOnOrigin: true,
    x: width / 2,
    y: 840,
  };
  const bedroomOverlay = {
    backgroundColor: 'rgba(0,0,0,0.67)',
    opacity: 0,
    zIndex: height,
  };
  const bedroomSign = {
    image: 'assets/ui/bedroom/bedroom_sleeping-sign.png',
    width: 261,
    height: 240,
    x: (width - 261) / 2,
    y: 265,
    zIndex: 2,
  };
  const bedroomSignTextHeight = 85;
  const bedroomSignText = <const>{
    font: 'Body',
    align: 'center',
    size: 32,
    color: '#CD5028',
    width: 204,
    height: bedroomSignTextHeight,
    centerOnOrigin: true,
    x: bedroomSign.width / 2 - 6,
    y: bedroomSign.height - bedroomSignTextHeight,
  };

  export const ui = {
    bedroom,
    bedroomBed,
    bedroomOverlay,
    bedroomSign,
    bedroomSignText,
  };
}

namespace minigame {
  const scene = {
    image: 'assets/minigame/match3/minigame.png',
  };
  const comboMessage = {
    x: width / 2,
    y: height * 0.4,
    align: 'center' as const,
    verticalAlign: 'center' as const,
    color: 'white',
    wordWrap: false,
    font: 'Title',
    width: 410,
    height: 40,
    centerOnOrigin: true,
    centerAnchor: true,
    visible: false,
    zIndex: 1320,
    r: degreesToRadians(-13),
    size: 72,
  } as const;

  const pointsMessage = {
    ...comboMessage,
    color: '#F19A84',
    y: comboMessage.y + 40,
  };

  export const style = {
    scene,
    comboMessage,
    pointsMessage,
  };
}

namespace menu {
  const menu = <const>{
    width,
    height: 780,
    infinite: true,
    image: 'assets/ui/shared/frames/popup_overlay.png',
    flex: {
      position: 'absolute',
      left: 0,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingBottom: 170,
    },
  };
  const menuRow = <const>{
    width: width * 0.75,
    flex: {
      marginTop: 6,
      justifyContent: 'space-evenly',
    },
    canHandleEvents: false,
  };
  const menuRowTwo = <const>{
    width: width * 0.75 * 0.67,
    flex: {
      marginTop: 6,
      justifyContent: 'space-evenly',
    },
    canHandleEvents: false,
  };
  const closeHintBg = {
    image: 'assets/ui/menu/menu_taptoclose.png',
    autoSize: true,
  };
  const closeHintText = <const>{
    color: '#FDFFEE',
    font: 'Body',
    size: 22,
    align: 'center',
    verticalAlign: 'center',
  };
  const menuButton = <const>{
    image: 'assets/ui/menu/menu_item_bg.png',
    font: 'Body',
    align: 'center',
    fontSize: 22,
    textColor: '#C57C58',
    labelHeight: 28,
    labelPaddingX: 20,
    width: 150,
    height: 169,
    labelOffsetX: -3,
    labelOffsetY: 107,
  };

  const icons = {
    encyclopedia: {
      image: `assets/ui/menu/menu_action_encyclopedia.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    petCollection: {
      image: `assets/ui/menu/menu_action_encyclopedia.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    growthJournal: {
      image: `assets/ui/menu/menu_action_encyclopedia.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    familyTree: {
      image: `assets/ui/menu/menu_action_familytree.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    mission: {
      image: `assets/ui/menu/menu_action_mission.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    decor: {
      image: `assets/ui/menu/menu_action_decor.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    profile: {
      image: `assets/ui/menu/menu_action_profile.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    settings: {
      image: `assets/ui/menu/menu_action_settings.png`,
      height: 71,
      width: 102,
      offset: { x: 0, y: -27 },
    },
    coins: {
      image: `assets/ui/menu/menu_shop_coin_icon.png`,
      height: 58,
      width: 58,
      offset: { x: 0, y: -27 },
    },
    energy: {
      image: `assets/ui/menu/menu_shop_energy_icon.png`,
      height: 61,
      width: 62,
      offset: { x: 0, y: -27 },
    },
    gems: {
      image: `assets/ui/menu/menu_shop_diamond_icon.png`,
      height: 83,
      width: 73,
      offset: { x: 0, y: -27 },
    },
    catalog: {
      image: `assets/ui/menu/menu_action_catalog.png`,
      height: 81,
      width: 79,
      offset: { x: 0, y: -27 },
    },
    previewStage: {
      image: 'assets/ui/menu/menu_action_stage.png',
      height: 59,
      width: 95,
      offset: { x: 0, y: -27 },
    },
  };

  export const ui = {
    menu,
    menuRow,
    menuRowTwo,
    menuButton,
    closeHintBg,
    closeHintText,
    icons,
  };
}

namespace tutorial {
  const frame = <const>{
    image: 'assets/ui/tutorial/tutorial_popup_frame_half.png',
    imgScale: 0.66,
    width: 323,
    height: 1202,
    scaleMethod: '3slice',
    sourceSlices: {
      vertical: { top: 112, bottom: 210 },
    },
  };

  const popup = {
    zIndex: 10000,
    width: frame.width * 2,
    height: frame.height,
    darkerBg: false,
    skipTitle: true,
    boxOpts: {
      image: null,
    },
  };

  const message = {
    color: '#3C73A3',
    size: 42,
    height: 56,
    x: 90,
    y: 18,
    zIndex: 1,
  };

  const pageIndicator = <const>{
    size: 24,
    font: 'Body',
    color: message.color,
    height: 48,
    width: 48,
    verticalAlign: 'center',
    align: 'center',
    centerOnOrigin: true,
    x: 320,
    y: 1040,
  };

  const navButton = <const>{
    y: 1012,
    width: 90,
    height: 68,
  };
  const prevButton = {
    ...buttons.navPrevious,
    ...navButton,
    x: 72,
  };
  const nextButton = {
    ...buttons.navNext,
    ...navButton,
    x: 480,
  };
  const closeButton = <const>{
    fontSize: 42,
    labelOffsetX: -4,
    labelOffsetY: -2,
    font: 'Body',
    ...buttons.secondary,
    disabledFontOffsetY: 0,
    width: 230,
    height: 89,
    x: popup.width / 2,
    y: 1120,
    centerOnOrigin: true,
  };

  type DeprecatedTutorialStep =
    | 'decorationBonus'
    | 'encyclopedia'
    | 'throwBuruBuru'
    | 'decorationProgression'
    | 'bubbleSlot'
    | 'transform'
    | 'accessory';

  export const images: Record<
    Exclude<TutorialStep, DeprecatedTutorialStep>,
    Record<Locale, string[]>
  > = {
    basicGameplay: {
      en: ['assets/tutorial/Tut1_EN.png', 'assets/tutorial/Tut2_EN.png'],
      ja: ['assets/tutorial/Tut1_JP.png', 'assets/tutorial/Tut2_JP.png'],
    },
    chatWithPets: {
      en: ['assets/tutorial/Tut3_EN.png', 'assets/tutorial/Tut4_EN.png'],
      ja: ['assets/tutorial/Tut3_JP.png', 'assets/tutorial/Tut4_JP.png'],
    },
    //never used in game beyond cheat's menu
    careForPets: {
      en: [],
      ja: [],
    },
    friendReferral: {
      en: ['assets/tutorial/Tut10_EN.png'],
      ja: ['assets/tutorial/Tut10_JP.png'],
    },
    profile: {
      en: ['assets/tutorial/Tut11_EN.png', 'assets/tutorial/Tut12_EN.png'],
      ja: ['assets/tutorial/Tut11_JP.png', 'assets/tutorial/Tut12_JP.png'],
    },
    // @deprecated
    match3: {
      en: ['assets/tutorial/Tut9_EN.png'],
      ja: ['assets/tutorial/Tut9_JP.png'],
    },
    adult: {
      en: ['assets/tutorial/Tut13_EN.png'],
      ja: ['assets/tutorial/Tut13_JP.png'],
    },
    visitFriend: {
      en: ['assets/tutorial/Tut14_EN.png'],
      ja: ['assets/tutorial/Tut14_JP.png'],
    },
    duoInvite: {
      en: ['assets/tutorial/Tut15_EN.png'],
      ja: ['assets/tutorial/Tut15_JP.png'],
    },
    duoInviteReceived: {
      en: ['assets/tutorial/Tut16_EN.png'],
      ja: ['assets/tutorial/Tut16_JP.png'],
    },
    decoration: {
      en: ['assets/tutorial/Tut17_EN.png'],
      ja: ['assets/tutorial/Tut17_JP.png'],
    },
    daycare: {
      en: ['assets/tutorial/Tut18_EN.png'],
      ja: ['assets/tutorial/Tut18_JP.png'],
    },
    catalog: {
      en: ['assets/tutorial/Tut27_EN.png'],
      ja: ['assets/tutorial/Tut27_JP.png'],
    },

    growthCapsule: {
      en: ['assets/tutorial/Tut29_EN.png'],
      ja: ['assets/tutorial/Tut29_JP.png'],
    },

    // @deprecated
    slots: {
      en: ['assets/tutorial/Tut33_EN.png'],
      ja: ['assets/tutorial/Tut33_JP.png'],
    },

    match2: {
      en: ['assets/tutorial/Tut38_EN.png'],
      ja: ['assets/tutorial/Tut38_JP.png'],
    },

    // @deprecated
    bumbum: {
      en: ['assets/tutorial/Tut40A_EN.png', 'assets/tutorial/Tut40B_EN.png'],
      ja: ['assets/tutorial/Tut40A_JP.png', 'assets/tutorial/Tut40B_JP.png'],
    },

    // @deprecated
    petCollection: {
      en: ['assets/tutorial/Tut42_EN.png'],
      ja: ['assets/tutorial/Tut42_JP.png'],
    },

    // @deprecated
    costumeBoost: {
      en: ['assets/tutorial/Tut44_EN.png'],
      ja: ['assets/tutorial/Tut44_JP.png'],
    },

    // @deprecated
    friendship: {
      en: ['assets/tutorial/Tut46_EN.png'],
      ja: ['assets/tutorial/Tut46_JP.png'],
    },

    // No images needed for these.
    teams: {
      en: [],
      ja: [],
    },

    // @deprecated
    makeHappy: {
      en: ['assets/tutorial/Tut47_EN.png'],
      ja: ['assets/tutorial/Tut47_JP.png'],
    },

    puzzleCoinsBasic: {
      en: ['assets/ui/puzzleCoins/tutorial_en.png'],
      ja: ['assets/ui/puzzleCoins/tutorial_ja.png'],
    },

    // No images needed for these.
    puzzleCoinsCare: {
      en: [],
      ja: [],
    },

    // No images needed for these.
    addFriends: {
      en: [],
      ja: [],
    },
  };

  export const ui = {
    popup,
    frame,
    message,
    pageIndicator,
    prevButton,
    nextButton,
    closeButton,
    images,
  };
}

namespace social {
  export namespace invite {
    const popup = {
      zIndex: 10000,
      width: popups.buruburu.width,
      height: 700,
      darkerBg: false,
    };

    namespace friendTile {
      export const container: FlexBoxImageScaleViewOpts = {
        image: 'assets/ui/shared/frames/scroller_panel_blue.png',
        height: 114,
        scaleMethod: '9slice',
        sourceSlices: {
          vertical: {
            top: 19,
            bottom: 28,
          },
          horizontal: {
            left: 19,
            right: 22,
          },
        },
        flex: {
          flexGrow: 1,
          alignItems: 'center',
          paddingBottom: 4,
        },
      };

      export const image = <const>{
        scaleMethod: 'contain',
        width: 78,
        height: 78,
      };

      export const name = <const>{
        size: 30,
        font: 'Body',
        color: '#9A7171',
        verticalAlign: 'top',
        width: 211,
        height: 36,
        y: 18,
      };

      export const amount = <const>{
        size: 32,
        font: 'Body',
        color: '#7191AB',
        x: 48,
        y: 59,
        width: 75,
        height: 34,
        align: 'left',
      };

      export const currencyGems = <const>{
        y: 48,
        width: 48,
        height: 48,
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_gem.png',
      };

      export const currencyEnergy = <const>{
        y: 51,
        width: 39,
        height: 46,
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_energy.png',
      };

      export const currencyEventEnergy = <const>{
        y: 51,
        width: 39,
        height: 46,
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_energy.png',
      };

      export const moves = <const>{
        y: 51,
        width: 39,
        height: 43,
        scaleMethod: 'contain',
        image: 'assets/ui/match2/common/moves.png',
      };

      export const cta = <const>{
        ...buttons.secondary,
        font: 'Body',
        fontSize: 30,
        width: 130,
        height: 72,
      };
    }

    export namespace confirm {
      export const popup = {
        zIndex: 10000,
        width: popups.buruburu.width,
        height: 549,
        darkerBg: false,
      };

      export const message = {
        height: 50,
        x: 40,
        y: 80,
        zIndex: 1,
      };

      export const underline = <const>{
        height: 5,
        x: message.x,
        y: 130,
        image: 'assets/ui/popups/panel_underline.png',
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 5, right: 5 },
        },
      };

      const friendTileContainer = friendTile.container;
      export const friendPanel = {
        image: friendTileContainer.image,
        slices: {
          top: friendTileContainer.sourceSlices!.vertical!.top!,
          bot: friendTileContainer.sourceSlices!.vertical!.bottom!,
          left: friendTileContainer.sourceSlices!.horizontal!.left!,
          right: friendTileContainer.sourceSlices!.horizontal!.right!,
        },
        width: popups.buruburu.width - 100,
        height: 172,
        x: 50,
        y: 166,
      };

      export const icon = <const>{
        scaleMethod: 'contain',
        x: 20,
        y: 20,
        width: 124,
        height: 124,
      };

      export const name = <const>{
        font: 'Body',
        color: '#9A7171',
        y: 64,
        size: 32,
        x: 165,
        height: 39,
        width: 283,
        verticalAlign: 'bottom',
      };

      export const notice = <const>{
        ...message,
        font: 'Body',
        color: '#BF8147',
        size: 24,
        align: 'center',
        y: 372,
      };

      const commandButton = {
        y: 429,
      };
      export const okButton = {
        ...confirmCareItem.ui.okButton,
        ...commandButton,
      };
      export const cancelButton = {
        ...confirmCareItem.ui.backButton,
        ...commandButton,
      };
    }

    export const ui = {
      popup,
      friendTile,
      confirm,
    };
  }
}

namespace profile {
  export namespace edit {
    export const noticeText = {
      color: '#dab07e',
      size: 18,
      align: 'justify',
      centerOnOrigin: true,
      font: 'Body',
      height: 130,
      canHandleEvents: true,
    } as const;

    export const input = {
      height: 61,
      centerOnOrigin: true,
      inputStyle: {
        background: '#fcf0d3',
        borderRadius: '10px',
        color: '#b68351',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '10vm',
      },
    };

    export const avatarSize = 140;
    export const avatarOutlineSize = 8;

    export const avatarButton = {
      width: avatarSize - avatarOutlineSize,
      height: avatarSize - avatarOutlineSize,
      x: avatarOutlineSize / 2,
      y: avatarOutlineSize / 2,
    };

    export const avatarOutline = {
      width: avatarSize,
      height: avatarSize,
    };
  }

  export namespace profile {
    export const contentPanel = {
      image: 'assets/ui/shared/frames/panel_overlay.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 27, bottom: 27 },
      },
      height: 47,
      centerOnOrigin: true,
    } as const;

    export const frameMargin = 20;

    export const titleText = {
      color: '#b48454',
      size: 25,
      align: 'left',
      verticalAlign: 'center',
      centerOnOrigin: true,
      font: 'Body',
    } as const;

    export const dataText = {
      color: '#947372',
      size: 25,
      align: 'right',
      verticalAlign: 'center',
      centerOnOrigin: true,
      font: 'Body',
    } as const;
  }

  const button = {
    labelOffsetY: -1,
    fontSize: 46,
    width: 240,
    height: 100,
    centerOnOrigin: true,
    font: 'Body',
  } as const;

  export const ui = {
    profile,
    edit,
    button,
  };
}

export default {
  backgroundDefaults: {
    width,
    height,
    horizontalAlign: 'center',
  },

  debut: {
    image: 'assets/ui/debut/debut-room.png',
  },

  livingRoom: {
    image: 'assets/ui/livingroom/living-room.png',
  },

  diningRoom: {
    image: 'assets/ui/diningroom/dining-room.png',
  },

  diningRoomMat: {
    image: 'assets/ui/diningroom/dining-room_mat.png',
    width: 529,
    height: 172,
    centerOnOrigin: true,
    x: width / 2,
    y: 934,
  },

  diningRoomMatDouble: {
    image: 'assets/ui/diningroom/dining-room_mat-double.png',
    width: 600,
    height: 194,
    centerOnOrigin: true,
    x: width / 2,
    y: 915,
  },

  bathroom: {
    image: 'assets/ui/bathroom/bathroom.png',
  },

  bathroomBathTop: {
    image: 'assets/ui/bathroom/bathroom_bath_top.png',
    width: 501,
    height: 220,
    centerOnOrigin: true,
    x: 364,
    y: 898,
  },

  bathroomBathFront: {
    image: 'assets/ui/bathroom/bathroom_bath_front.png',
    width: 501,
    height: 220,
    centerOnOrigin: true,
    x: 364,
    y: 898,
    zIndex: height,
  },

  toiletRoom: {
    image: 'assets/ui/toilet/toilet-room.png',
  },
  toiletRoomToilet: {
    image: 'assets/ui/toilet/toilet-room_toilet.png',
    width: 360,
    height: 573,
    centerOnOrigin: true,
    x: 367,
    y: 707,
  },

  ...bedroom.ui,

  frame: {
    autoSize: true, // 720 x variable height
    // vertical offset and anchor will be dynamically set to bottom after creation
    centerOnOrigin: true,
    centerAnchor: true,
  },

  menu: menu.ui,

  minigame: minigame.style,

  friends: {
    actionIcons: {
      invite: {
        image: `assets/ui/friends/friend_action_referral.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      duoReceivedList: {
        image: `assets/ui/duo/icons/duo_received.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      duoSentList: {
        image: `assets/ui/duo/icons/duo_sent.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      gift: {
        image: `assets/ui/friends/friend_action_gift.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      visitorList: {
        image: `assets/ui/friends/friend_action_visitorslist.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      visitFriend: {
        image: `assets/ui/friends/friend_action_visitfriend.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      teamCafe: {
        image: `assets/ui/friends/team_cafe.png`,
        height: 88,
        width: 66,
        offset: { x: 0, y: -27 },
      },
      chat: {
        image: `assets/ui/chatbot/menu_action_chat.png`,
        height: 120,
        width: 78,
        offset: { x: 0, y: -27 },
      },
      friendRequests: {
        image: `assets/ui/friends/icon_friend_requests.png`,
        width: downscale(155),
        height: downscale(111),
        offset: { x: 0, y: -32 },
      },
      addFriends: {
        image: `assets/ui/friends/icon_add_friends.png`,
        width: downscale(113),
        height: downscale(104),
        offset: { x: 0, y: -27 },
      },
      addFriendsV2: {
        image: `assets/ui/friends/icon_add_friends_v2.png`,
        width: downscale(113),
        height: downscale(104),
        offset: { x: 0, y: -27 },
      },
      fortune: {
        image: `assets/ui/friends/icon_fortune.png`,
        width: 64,
        height: 67,
        offset: { x: 7, y: -30 },
      },
    },
  },

  care: {
    actionIcons: {
      food: {
        image: `assets/ui/care/care_action_food.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      snack: {
        image: `assets/ui/care/care_action_snack.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      toy: {
        image: `assets/ui/care/care_action_toy.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      firstAid: {
        image: `assets/ui/care/care_action_firstaid.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      bath: {
        image: `assets/ui/care/care_action_bath.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      toilet: {
        image: `assets/ui/care/care_action_toilet.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
      growthcapsule: {
        image: `assets/ui/growthacceleration/action_growthcapsule_icon.png`,
        height: 73,
        width: 82,
        offset: { x: 0, y: -27 },
      },
      makehappy: {
        image: `assets/ui/care/care_action_makehappy.png`,
        height: 71,
        width: 102,
        offset: { x: 0, y: -27 },
      },
    },
    itemIcons: careItemIcons,
    itemButton: {
      container: {
        image: 'assets/ui/care/care_item_cell.png',
        width: 238,
        height: 136,
      },
      image: {
        scaleMethod: 'contain',
        width: 84,
        height: 102,
        x: 15,
        y: 15,
      },
      name: {
        size: 20,
        font: 'Body',
        color: '#9A7171',
        verticalAlign: 'top',
        width: 106,
        height: 48,
        x: 107,
        y: 25,
      },
      price: {
        size: 20,
        font: 'Body',
        color: '#9A7171',
        x: 115,
        y: 92,
        width: 75,
        height: 24,
        align: 'right',
      },
      currency: {
        x: 192,
        y: 84,
        width: 38,
        height: 38,
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_coin.png',
      },
      icons: careItemIcons,
    },
    confirmation: confirmCareItem.ui,
  },

  tutorial: tutorial.ui,

  invite: social.invite.ui,
  profile: profile.ui,
} as const;
